import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import AppContext from "../Context/Context";
import LoadingAnimation from "../LoadingAnimation/LoadingAnimation";
import axiosAuth from "../Axios/Interceptors";
import { showErrorMessage } from "../Shared/Toast";

type DragDropProps = {
  ParentFeatures: any;
  DragDropCallback: any;
  TriggerChild: any;
};

export default function DragDrop(props: DragDropProps) {
  const [LoadingState, SetLoadingState] = useState(false);
  const [files, setFiles] = useState<any | null>([]);
  const [MaxAllowedFiles, SetMaxAllowedFiles] = useState(
    props.ParentFeatures.MaxAllowedFiles
  );
  const [IsDisable, SetIsDisable] = useState(false);
  const [PTUploadedFiles, SetPTUploadedFiles] = useState(
    props.ParentFeatures.PTUploadedFiles
  );
  const FilesRef: any = useRef(null);
  FilesRef.current = files;

  const PTUploadedFilesRef = useRef<any | null>(null);
  PTUploadedFilesRef.current = PTUploadedFiles;

  const didMount = useRef(false);

  useEffect(() => {
    if (props.ParentFeatures && props.ParentFeatures.PTUploadedFiles.length > 0)
      SetPTUploadedFiles(props.ParentFeatures.PTUploadedFiles);
    props.DragDropCallback(props.ParentFeatures.PTUploadedFiles);
    SetIsDisable(false);
    if (didMount.current) {
      props.DragDropCallback([]);
    } else {
      didMount.current = true;
    }
  }, [props.TriggerChild, props.ParentFeatures.PTUploadedFiles]);

  const { getRootProps, getInputProps, isDragAccept, isDragReject, isFocused } =
    useDropzone({
      maxFiles: MaxAllowedFiles,
      disabled: IsDisable,
      onDrop: (acceptedFiles) => {
        if (acceptedFiles.length === MaxAllowedFiles) {
          SetIsDisable(true);
        } else {
          SetIsDisable(false);
        }

        setFiles(
          acceptedFiles.map((file) =>
            Object.assign(file, { preview: URL.createObjectURL(file) })
          )
        );
        FilesRef.current = acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        );
        //console.log(FilesRef.current);
        UploadFiles();
      },

      onDropRejected: (event) => {
        if (event.length > MaxAllowedFiles) {
          showErrorMessage("only " + MaxAllowedFiles + " are allowed");
        } else {
          showErrorMessage(event[0].errors[0].message);
        }

        if (files.length === MaxAllowedFiles) {
          SetIsDisable(true);
        } else {
          SetIsDisable(false);
        }
      },
    });

  const RemoveFile = (index: any) => {
    const newFiles = [...PTUploadedFiles];
    newFiles.splice(index, 1);
    SetPTUploadedFiles(newFiles);
    props.DragDropCallback(newFiles);
    if (newFiles.length === MaxAllowedFiles) {
      SetIsDisable(true);
    } else {
      SetIsDisable(false);
    }
  };

  async function UploadFiles() {
    if (
      FilesRef.current.length > 0 &&
      PTUploadedFiles.length + FilesRef.current.length <= MaxAllowedFiles
    ) {
      for (let i = 0; i < FilesRef.current.length; i++) {

        const file = FilesRef.current[i];
        const { width, height,ratio } = await getImageMeta(file);
        // console.log("Image Width:", width);
        // console.log("Image Height:", height);
        // console.log("Image Ratio:", ratio);

        const url = AppContext.FilesAPI + "FileUploader/UploadFile";
        let FileNewName =
          AppContext.GenerateUniqueGUID() + "_" + FilesRef.current[i].name;
        let FileNameWithoutSpaces = FileNewName.replace(/\s/g, "");
        let FilePath =
          "Uploads/" +
          props.ParentFeatures.FolderName +
          "/" +
          FileNameWithoutSpaces;
        let OtherParams =
          "1," +
          props.ParentFeatures.ObjectType +
          "," +
          props.ParentFeatures.ObjectID +
          ",1,mnv";

        let formData = new FormData();
        formData.append("file", FilesRef.current[i], FileNameWithoutSpaces);
        formData.append("UploadType", props.ParentFeatures.UploadType);
        formData.append("FolderName", props.ParentFeatures.FolderName);
        formData.append("OtherParams", OtherParams);
        SetLoadingState(true);
        await axiosAuth.post(url, formData).then((response) => {
          if (response.data.Status === 1) {
            if (response.data.Data[0].Status === 1) {
              let object = {} as any;
              object.Name = response.data.Data[0].FileName;
              object.FileURL = response.data.Data[0].FilePath;
              object.FilePathFull = response.data.Data[0].FilePathFull;
              object.SysObjID = response.data.Data[0].SysObjID;
              object.ObjectID = props.ParentFeatures.ObjectID;
              object.Ratio = ratio;
              object.FileURLDisplay = AppContext.GetFileIconToDisplay(
                response.data.Data[0].FilePathFull
              );

              const l_PTUploadedFilesRef = [...PTUploadedFilesRef.current];
              l_PTUploadedFilesRef.push(object);

              SetPTUploadedFiles(l_PTUploadedFilesRef);
              props.DragDropCallback(l_PTUploadedFilesRef);
              if (l_PTUploadedFilesRef.length === MaxAllowedFiles) {
                SetIsDisable(true);
              } else {
                SetIsDisable(false);
              }
            } else {
              SetIsDisable(false);
              showErrorMessage(response.data.Data[0].Message);
            }
          }
          SetLoadingState(false);
        });
      }
    } else {
      showErrorMessage("only " + MaxAllowedFiles + " are allowed");
      if (PTUploadedFiles.length === MaxAllowedFiles) {
        SetIsDisable(true);
      } else {
        SetIsDisable(false);
      }
    }
  }

const getImageMeta = (file: File): Promise<{
  width: number;
  height: number;
  ratio: number;
}> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      resolve({
        width,
        height,
        ratio: +(height/width).toFixed(4), // rounded
      });

      URL.revokeObjectURL(img.src);
    };

    img.onerror = reject;
  });
};


  const baseStyle = {
    display: "inline-block",
    borderWidth: 1,
    borderRadius: 0,
    borderColor: "#cacaca",
    borderStyle: "dashed",
    backgroundColor: "#fff",
    color: "#263a5d",
    outline: "none",
    transition: "border .24s ease-in-out",
    width: "100%",
  };

  const focusedStyle = {
    borderColor: "#64278c",
    backgroundColor: "#f1ebf4",
  };

  const acceptStyle = {
    borderColor: "#64278c",
    backgroundColor: "#f1ebf4",
  };

  const rejectStyle = {
    borderColor: "#64278c",
    backgroundColor: "#f1ebf4",
  };

  const Files = PTUploadedFiles.map((file: any, index: any) => (
    <div className="remove--image" key={index}>
      <img
        key={file.Name}
        src={file.FileURLDisplay}
        alt="image"
        width="200"
        height="200"
      />
      <a onClick={() => RemoveFile(index)}>
        <img
          className=""
          src="https://dft8zj8i6wy63.cloudfront.net/mnv/images/cross.svg"
          alt="delete"
          width="9"
          height="17"
        />
      </a>
    </div>
  ));

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  return (
    <>
      <div className="drag-drop-main inline-block-d width-100">
        <div className="drag-drop p-relative" {...getRootProps({ style })}>
          {LoadingState && <LoadingAnimation />}
          <input {...getInputProps()} />
          <div className="txt-drag flex-dj">Drag & drop files here or</div>
          <div className="btn-browse flex-dj">
            <a className="btn-secondary flex-daj">Browse for file</a>
          </div>
        </div>
        {Files && Files.length > 0 && (
          <div className="contact-attachments wrap-flex">{Files}</div>
        )}
      </div>
    </>
  );
}
